import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { api } from '../../context/AuthContext';
import { toast } from 'sonner';

export default function RobotsTxtPage() {
  const [url, setUrl] = useState('');
  const [platform, setPlatform] = useState('default');
  const [blockAi, setBlockAi] = useState(false);
  const [crawlDelay, setCrawlDelay] = useState('');
  const [extraDisallows, setExtraDisallows] = useState('');
  const [sitemapUrls, setSitemapUrls] = useState('');
  const [platforms, setPlatforms] = useState([]);
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    api.get('/robots-txt/platforms').then((r) => setPlatforms(r.data?.platforms || [])).catch(() => {});
  }, []);

  const errDetail = (err) => {
    const d = err.response?.data?.detail;
    if (typeof d === 'string') return d;
    if (Array.isArray(d)) return d.map((x) => x.msg || x).join(', ');
    return err.message || 'Request failed';
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    setStatus('Generating robots.txt...');
    try {
      const { data } = await api.post('/robots-txt/generate', {
        url,
        platform,
        block_ai_crawlers: blockAi,
        crawl_delay: crawlDelay ? parseInt(crawlDelay) : null,
        extra_disallows: extraDisallows.split('\n').map((s) => s.trim()).filter(Boolean),
        sitemap_urls: sitemapUrls.split('\n').map((s) => s.trim()).filter(Boolean),
      });
      setResult(data);
      setStatus('Generated successfully!');
    } catch (err) {
      setStatus(`Error: ${errDetail(err)}`);
    } finally {
      setLoading(false);
    }
  };

  const copy = () => {
    if (!result?.content) return;
    navigator.clipboard.writeText(result.content);
    toast.success('robots.txt copied');
  };

  const download = () => {
    if (!result?.content) return;
    const blob = new Blob([result.content], { type: 'text/plain' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'robots.txt';
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const applyToSite = async () => {
    if (!result?.content) return;
    setApplying(true);
    try {
      const { data } = await api.post('/robots-txt/apply', {
        url,
        platform,
        block_ai_crawlers: blockAi,
        crawl_delay: crawlDelay ? parseInt(crawlDelay) : null,
        extra_disallows: extraDisallows.split('\n').map((s) => s.trim()).filter(Boolean),
        sitemap_urls: sitemapUrls.split('\n').map((s) => s.trim()).filter(Boolean),
        content: result.content,
      });
      toast.success(`Published to your site — live at ${data.robots_url}`);
      if (data.detail?.warning) toast.warning(data.detail.warning, { duration: 9000 });
    } catch (err) {
      toast.error(errDetail(err), { duration: 9000 });
    } finally {
      setApplying(false);
    }
  };

  return (
    <motion.div className="space-y-6 p-4 md:p-6" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Robots.txt Generator</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1 text-sm">Generate a recommended robots.txt tuned for your site platform.</p>
      </div>

      <form onSubmit={handleGenerate} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6 shadow-sm space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Target URL</label>
            <input
              type="url" value={url} onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com" required
              className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Platform</label>
            <select
              value={platform} onChange={(e) => setPlatform(e.target.value)}
              className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
            >
              {platforms.map((p) => (
                <option key={p.id} value={p.id}>{p.id}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Extra Disallow Paths (one per line)</label>
            <textarea
              rows={3} value={extraDisallows} onChange={(e) => setExtraDisallows(e.target.value)}
              placeholder="/private/&#10;/tmp/"
              className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm font-mono"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Sitemap URLs (one per line)</label>
            <textarea
              rows={3} value={sitemapUrls} onChange={(e) => setSitemapUrls(e.target.value)}
              placeholder="https://example.com/sitemap.xml"
              className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm font-mono"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-4 items-center">
          <label className="inline-flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
            <input type="checkbox" checked={blockAi} onChange={(e) => setBlockAi(e.target.checked)} />
            Block AI crawlers (GPTBot, ClaudeBot, PerplexityBot, etc.)
          </label>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-slate-700 dark:text-slate-300">Crawl-delay (sec):</span>
            <input
              type="number" min="0" value={crawlDelay} onChange={(e) => setCrawlDelay(e.target.value)}
              className="w-20 p-1 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
            />
          </div>
        </div>

        <button type="submit" disabled={loading} className="bg-violet-600 text-white px-4 py-2 rounded-md text-sm hover:bg-violet-700 disabled:opacity-60">
          {loading ? 'Generating...' : 'Generate robots.txt'}
        </button>

        {status && (
          <div className={`p-3 rounded-md text-sm ${status.startsWith('Error') ? 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300' : 'bg-violet-50 text-violet-800 dark:bg-violet-950/40 dark:text-violet-200'}`}>
            {status}
          </div>
        )}
      </form>

      {result?.content && (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6 shadow-sm space-y-4">
          <div className="flex flex-wrap gap-2 items-center">
            <button type="button" onClick={copy} className="text-sm px-3 py-1.5 rounded-md bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700">Copy</button>
            <button type="button" onClick={download} className="text-sm px-3 py-1.5 rounded-md bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700">Download</button>
            <button type="button" onClick={applyToSite} disabled={applying} className="text-sm px-3 py-1.5 rounded-md bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-60">
              {applying ? 'Applying…' : 'Apply to site'}
            </button>
            <span className="text-xs text-slate-400">Publishes directly to your connected CMS</span>
          </div>
          <pre className="bg-slate-50 dark:bg-slate-900 p-4 rounded-md overflow-auto max-h-96 text-xs whitespace-pre-wrap font-mono text-slate-700 dark:text-slate-300">
            {result.content}
          </pre>
        </div>
      )}
    </motion.div>
  );
}
